import React, { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CreateUser from "./CreateUser";
import Labels from "../labels/Labels";
import RenderOnRole from "./RenderOnRole";
import { PersonOff } from "@mui/icons-material";

async function fetchSubDepartments(employerId) {
  if (!employerId) return { data: [] };
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/subdepartment/options?companyId=${employerId}`
  );
  return response;
}

async function fetchEmployerUsers(
  employerId,
  subDepartmentId = null,
  locationId = null,
  page = 0,
  pageSize = 5,
  filterString = null
) {
  const targetId =
    subDepartmentId && subDepartmentId !== "" ? subDepartmentId : employerId;

  // Use the new endpoint that filters by employer and location with pagination
  let url =
    window.API_URL +
    `/employer/users?employerId=${targetId}&page=${page}&pageSize=${pageSize}`;
  if (locationId) {
    url += `&locationId=${locationId}`;
  }
  if (filterString) {
    url += `&filterString=${encodeURIComponent(filterString)}`;
  }

  const response = await HttpService.getAxiosClient().get(url);
  return response;
}

async function deleteUser(username, employerIds, locationIds) {
  const response = await HttpService.getAxiosClient().post(
    window.API_URL +
      `/employer/user/${username}/remove-companies-and-locations`,
    {
      employerIds: employerIds || [],
      locationIds: locationIds || [],
    }
  );
  return response;
}

async function fetchUserLocations(username) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/user/${username}/locations`
  );
  return response;
}

async function addUserLocation(username, locationId) {
  const response = await HttpService.getAxiosClient().post(
    window.API_URL + `/employer/user/${username}/locations/${locationId}`
  );
  return response;
}

async function removeUserLocation(username, locationId) {
  const response = await HttpService.getAxiosClient().delete(
    window.API_URL + `/employer/user/${username}/locations/${locationId}`
  );
  return response;
}

async function fetchAvailableLocations(employerId) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/locations?employerId=${employerId}`
  );
  return response;
}

async function fetchUserRelatedCompanies(username) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/user/${username}/companies`
  );
  return response;
}

async function fetchUserRoles(username, employerId) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL +
      `/employer/user/${encodeURIComponent(username)}/roles?employerId=${encodeURIComponent(employerId)}`
  );
  return response;
}

async function addUserCompany(username, companyId) {
  const response = await HttpService.getAxiosClient().post(
    window.API_URL + `/employer/user/${username}/companies/${companyId}`
  );
  return response;
}

async function removeUserCompany(username, companyId) {
  const response = await HttpService.getAxiosClient().delete(
    window.API_URL + `/employer/user/${username}/companies/${companyId}`
  );
  return response;
}

async function resetUserPassword(username, employerId) {
  const response = await HttpService.getAxiosClient().post(
    window.API_URL + `/employer/user/reset-password`,
    {
      username: username,
      employerId: employerId,
    }
  );
  return response;
}

async function disableUser(username, employerId) {
  const response = await HttpService.getAxiosClient().put(
    window.API_URL +
      `/employer/user/${username}/disable?employerId=${employerId}`
  );
  return response;
}

/**
 * Reusable Users Management Grid Component
 *
 * @param {Object} props
 * @param {string} props.employerId - The employer ID to fetch users for
 * @param {Object} [props.employer] - Optional employer object (contains idEmployer and name)
 * @param {boolean} [props.showAddButton=true] - Whether to show the "Add Users" button
 * @param {boolean} [props.showSubDepartmentFilter=true] - Whether to show subdepartment filter
 * @param {string} [props.height] - Height of the DataGrid container (default: "calc(100vh - 350px)")
 */
export default function UsersManagementGrid({
  employerId,
  employer,
  showAddButton = true,
  showSubDepartmentFilter = true,
  height = "calc(100vh - 350px)",
}) {
  const queryClient = useQueryClient();
  const [selectedSubDepartment, setSelectedSubDepartment] = useState("");
  const [createUserDlg, setCreateUserDlg] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [userToDisable, setUserToDisable] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [rolesDialog, setRolesDialog] = useState({
    open: false,
    username: null,
    userDisplayName: null,
  });
  const [selectedLocations, setSelectedLocations] = useState(null);
  const [locationsDialog, setLocationsDialog] = useState({
    open: false,
    username: null,
    userDisplayName: null,
  });
  const [addLocationDialog, setAddLocationDialog] = useState(false);
  const [subdepartmentsDialog, setSubdepartmentsDialog] = useState({
    open: false,
    username: null,
    userDisplayName: null,
  });
  const [addSubdepartmentDialog, setAddSubdepartmentDialog] = useState(false);
  const [removeSubdepartmentDialog, setRemoveSubdepartmentDialog] = useState({
    open: false,
    employerId: null,
    employerName: null,
  });
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // Filter state for server-side filtering
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  // Fetch subdepartments for the employer
  const { data: subDepartmentsData, isLoading: isLoadingSubDepartments } =
    useQuery({
      queryKey: ["subDepartments", employerId],
      queryFn: () => fetchSubDepartments(employerId),
      enabled: !!employerId,
    });

  // Extract filter string from filterModel
  const filterString = filterModel.quickFilterValues?.join(" ") || "";

  // Minimum search length requirement
  const MIN_SEARCH_LENGTH = 5;
  const isSearchValid =
    !filterString || filterString.length >= MIN_SEARCH_LENGTH;

  // Reset pagination when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [selectedLocations?.id, selectedSubDepartment]);

  // Fetch users for employer/subdepartment filtered by location with pagination
  const {
    data: usersData,
    isLoading: isInitialLoadingUsers,
    isFetching: isLoadingUsers,
  } = useQuery({
    queryKey: [
      "employerUsers",
      employerId,
      selectedSubDepartment,
      selectedLocations?.id,
      paginationModel.page,
      paginationModel.pageSize,
      filterString,
    ],
    queryFn: () =>
      fetchEmployerUsers(
        employerId,
        selectedSubDepartment,
        selectedLocations?.id,
        paginationModel.page,
        paginationModel.pageSize,
        filterString
      ),
    enabled: !!employerId && isSearchValid,
    keepPreviousData: true, // Keep previous data while loading new page
  });

  // Fetch locations for a user
  const { data: userLocationsData, isLoading: isLoadingLocations } = useQuery({
    queryKey: ["userLocations", locationsDialog.username],
    queryFn: () => fetchUserLocations(locationsDialog.username),
    enabled: !!locationsDialog.username && locationsDialog.open,
  });

  // Fetch available locations for the employer
  const {
    data: availableLocationsData,
    isLoading: isLoadingAvailableLocations,
  } = useQuery({
    queryKey: ["availableLocations", employerId],
    queryFn: async () => {
      const response = await fetchAvailableLocations(employerId);
      // Add "ANY - All Locations" option with id -1
      const anyLocation = {
        id: -1,
        description: "ANY - All Locations",
        address_line_1: "",
        city: "",
        state: "",
      };
      return {
        ...response,
        data: [anyLocation, ...(response.data || [])],
      };
    },
    enabled: !!employerId,
  });

  // Fetch employers for a user
  const { data: userCompaniesData, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ["userCompanies", subdepartmentsDialog.username],
    queryFn: () => fetchUserRelatedCompanies(subdepartmentsDialog.username),
    enabled: !!subdepartmentsDialog.username && subdepartmentsDialog.open,
  });

  const {
    data: userRolesData,
    isLoading: isLoadingRoles,
    isError: isRolesError,
  } = useQuery({
    queryKey: ["userRoles", rolesDialog.username, employerId],
    queryFn: () => fetchUserRoles(rolesDialog.username, employerId),
    enabled: !!rolesDialog.username && rolesDialog.open,
  });

  const sortedUserRoles = Array.from(
    new Set(Array.isArray(userRolesData?.data) ? userRolesData.data : [])
  ).sort((firstRole, secondRole) =>
    firstRole.localeCompare(secondRole, undefined, { sensitivity: "base" })
  );

  // Fetch available subdepartments for current employer context
  const {
    data: availableSubdepartmentsData,
    isLoading: isLoadingAvailableSubdepartments,
  } = useQuery({
    queryKey: ["availableSubdepartments", employerId],
    queryFn: () => fetchSubDepartments(employerId),
    enabled: !!employerId && subdepartmentsDialog.open,
  });

  // Delete user mutation
  const deleteUserMutation = useMutation(
    ({ username, employerIds, locationIds }) =>
      deleteUser(username, employerIds, locationIds),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.userRemovedSuccess",
            "User removed successfully"
          ),
        });
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        queryClient.invalidateQueries({ queryKey: ["employerUsers"] });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            Labels.getLabel(
              "usersGrid.feedback.userRemovedError",
              "Error removing user. Please try again."
            ),
        });
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      },
    }
  );

  // Add location mutation
  const addLocationMutation = useMutation(
    ({ username, locationId }) => addUserLocation(username, locationId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.locationAddedSuccess",
            "Location added successfully"
          ),
        });
        queryClient.invalidateQueries({
          queryKey: ["userLocations", locationsDialog.username],
        });
        setAddLocationDialog(false);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            Labels.getLabel(
              "usersGrid.feedback.locationAddedError",
              "Error adding location. Please try again."
            ),
        });
      },
    }
  );

  // Remove location mutation
  const removeLocationMutation = useMutation(
    ({ username, locationId }) => removeUserLocation(username, locationId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.locationRemovedSuccess",
            "Location removed successfully"
          ),
        });
        queryClient.invalidateQueries({
          queryKey: ["userLocations", locationsDialog.username],
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            Labels.getLabel(
              "usersGrid.feedback.locationRemovedError",
              "Error removing location. Please try again."
            ),
        });
      },
    }
  );

  // Add subdepartment mutation
  const addSubdepartmentMutation = useMutation(
    ({ username, companyId }) => addUserCompany(username, companyId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.subdepartmentAddedSuccess",
            "Subdepartment added successfully"
          ),
        });
        queryClient.invalidateQueries({
          queryKey: ["userCompanies", subdepartmentsDialog.username],
        });
        setAddSubdepartmentDialog(false);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            Labels.getLabel(
              "usersGrid.feedback.subdepartmentAddedError",
              "Error adding subdepartment. Please try again."
            ),
        });
      },
    }
  );

  // Remove subdepartment mutation
  const removeSubdepartmentMutation = useMutation(
    ({ username, companyId }) => removeUserCompany(username, companyId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.subdepartmentRemovedSuccess",
            "Subdepartment removed successfully"
          ),
        });
        queryClient.invalidateQueries({
          queryKey: ["userCompanies", subdepartmentsDialog.username],
        });
        queryClient.invalidateQueries({ queryKey: ["employerUsers"] });
        setRemoveSubdepartmentDialog({
          open: false,
          companyId: null,
          companyName: null,
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            Labels.getLabel(
              "usersGrid.feedback.subdepartmentRemovedError",
              "Error removing subdepartment. Please try again."
            ),
        });
      },
    }
  );

  // Disable user mutation
  const disableUserMutation = useMutation(
    ({ username, employerId }) => disableUser(username, employerId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.userDisabledSuccess",
            "User disabled successfully"
          ),
        });
        setDisableDialogOpen(false);
        setUserToDisable(null);
        queryClient.invalidateQueries({ queryKey: ["employerUsers"] });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data ||
            Labels.getLabel(
              "usersGrid.feedback.userDisabledError",
              "Error disabling user. Please try again."
            ),
        });
        setDisableDialogOpen(false);
        setUserToDisable(null);
      },
    }
  );

  // Reset password mutation
  const resetPasswordMutation = useMutation(
    ({ username, employerId }) => resetUserPassword(username, employerId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "usersGrid.feedback.passwordResetSuccess",
            "Password reset successfully"
          ),
        });
        setResetPasswordDialogOpen(false);
        setUserToResetPassword(null);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            Labels.getLabel(
              "usersGrid.feedback.passwordResetError",
              "Error resetting password. Please try again."
            ),
        });
        setResetPasswordDialogOpen(false);
        setUserToResetPassword(null);
      },
    }
  );

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDisableClick = (user) => {
    setUserToDisable(user);
    setDisableDialogOpen(true);
  };

  const handleConfirmDisable = () => {
    if (userToDisable) {
      disableUserMutation.mutate({
        username: userToDisable.username,
        employerId: employerId,
      });
    }
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      // Use selected location if provided, otherwise send empty array
      const locationIds = selectedLocations
        ? [String(selectedLocations.id)]
        : [];

      // Use subdepartment if selected, otherwise use employer
      const targetEmployerId =
        selectedSubDepartment && selectedSubDepartment !== ""
          ? selectedSubDepartment
          : employerId;

      // Remove user from current employer/subdepartment and selected location
      deleteUserMutation.mutate({
        username: userToDelete.username,
        employerIds: [targetEmployerId],
        locationIds: locationIds,
      });
    }
  };

  const handleLocationsClick = (user) => {
    const displayName =
      user.firstName && user.lastName
        ? `${user.username} | ${user.firstName} ${user.lastName}`
        : user.username;
    setLocationsDialog({
      open: true,
      username: user.username,
      userDisplayName: displayName,
    });
  };

  const handleCloseLocationsDialog = () => {
    setLocationsDialog({
      open: false,
      username: null,
      userDisplayName: null,
    });
  };

  const handleAddLocation = (locationId) => {
    addLocationMutation.mutate({
      username: locationsDialog.username,
      locationId,
    });
  };

  const handleRemoveLocation = (locationId) => {
    removeLocationMutation.mutate({
      username: locationsDialog.username,
      locationId,
    });
  };

  const handleSubdepartmentsClick = (user) => {
    const displayName =
      user.firstName && user.lastName
        ? `${user.username} | ${user.firstName} ${user.lastName}`
        : user.username;
    setSubdepartmentsDialog({
      open: true,
      username: user.username,
      userDisplayName: displayName,
    });
  };

  const handleCloseSubdepartmentsDialog = () => {
    setSubdepartmentsDialog({
      open: false,
      username: null,
      userDisplayName: null,
    });
    setAddSubdepartmentDialog(false);
    setRemoveSubdepartmentDialog({
      open: false,
      employerId: null,
      employerName: null,
    });
  };

  const handleAddSubdepartment = (employerId) => {
    addSubdepartmentMutation.mutate({
      username: subdepartmentsDialog.username,
      companyId: employerId,
    });
  };

  const handleRemoveSubdepartmentClick = (employer) => {
    setRemoveSubdepartmentDialog({
      open: true,
      employerId: employer.idEmployer,
      employerName: employer.description || employer.name,
    });
  };

  const handleConfirmRemoveSubdepartment = () => {
    removeSubdepartmentMutation.mutate({
      username: subdepartmentsDialog.username,
      companyId: removeSubdepartmentDialog.employerId,
    });
  };

  const handleResetPasswordClick = (user) => {
    setUserToResetPassword(user);
    setResetPasswordDialogOpen(true);
  };

  const handleRolesClick = (user) => {
    const displayName =
      user.firstName && user.lastName
        ? `${user.username} | ${user.firstName} ${user.lastName}`
        : user.username;
    setRolesDialog({
      open: true,
      username: user.username,
      userDisplayName: displayName,
    });
  };

  const handleCloseRolesDialog = () => {
    setRolesDialog({ open: false, username: null, userDisplayName: null });
  };

  const handleConfirmResetPassword = () => {
    if (userToResetPassword) {
      // Use subdepartment if selected, otherwise use employer
      const targetEmployerId =
        selectedSubDepartment && selectedSubDepartment !== ""
          ? selectedSubDepartment
          : employerId;

      resetPasswordMutation.mutate({
        username: userToResetPassword.username,
        employerId: targetEmployerId,
      });
    }
  };

  // Get assigned location IDs for quick lookup
  const assignedLocationIds = new Set(
    userLocationsData?.data?.map((loc) => loc.id) || []
  );

  // Get available locations that are not yet assigned
  const unassignedLocations = (availableLocationsData?.data || []).filter(
    (location) => !assignedLocationIds.has(location.id)
  );

  // Get assigned employer IDs (subdepartments) for quick lookup
  const assignedEmployerIds = new Set(
    userCompaniesData?.data?.map(
      (emp) => emp.idEmployer || emp.idCompany || emp.id
    ) || []
  );

  // Get available subdepartments that are not yet assigned
  // Include parent employer and all subdepartments
  const allAvailableEmployers = employer
    ? [employer, ...(availableSubdepartmentsData?.data || [])]
    : availableSubdepartmentsData?.data || [];

  const unassignedSubdepartments = allAvailableEmployers.filter(
    (emp) => !assignedEmployerIds.has(emp.idEmployer || emp.idCompany || emp.id)
  );

  const usersColumns = [
    {
      field: "actions",
      headerName: Labels.getLabel("usersGrid.column.actions", "Actions"),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isAnyMutationLoading =
          deleteUserMutation.isLoading ||
          disableUserMutation.isLoading ||
          resetPasswordMutation.isLoading;
        const isUserDisabled = params.row.enabled === false;
        const isDisabled = isAnyMutationLoading || isUserDisabled;
        return (
          <Box display="flex" gap={1}>
            <Tooltip
              title={Labels.getLabel(
                "usersGrid.tooltip.manageLocations",
                "View/Manage Locations"
              )}
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleLocationsClick(params.row)}
                  disabled={isDisabled}
                >
                  <LocationOnIcon />
                </IconButton>
              </span>
            </Tooltip>
            <RenderOnRole roles={["reset-password"]}>
              <Tooltip
                title={Labels.getLabel(
                  "usersGrid.tooltip.resetPassword",
                  "Reset Password"
                )}
              >
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => handleResetPasswordClick(params.row)}
                    disabled={isDisabled}
                  >
                    <LockResetIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </RenderOnRole>
            <Tooltip title="View Keycloak roles">
              <span>
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handleRolesClick(params.row)}
                  disabled={isDisabled}
                >
                  <AssignmentIndIcon />
                </IconButton>
              </span>
            </Tooltip>
            <RenderOnRole
              roles={["pc-program-super-admin", "config-ou-for-user"]}
            >
              <Tooltip
                title={Labels.getLabel(
                  "usersGrid.tooltip.manageSubdepartments",
                  "View/Manage Units"
                )}
              >
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleSubdepartmentsClick(params.row)}
                    disabled={isDisabled}
                  >
                    <BusinessIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </RenderOnRole>
            <RenderOnRole roles={["pc-program-super-admin", "disable-user"]}>
              <Tooltip
                title={Labels.getLabel(
                  "usersGrid.tooltip.disableUser",
                  "Disable User"
                )}
              >
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDisableClick(params.row)}
                    disabled={isDisabled}
                  >
                    <PersonOff />
                  </IconButton>
                </span>
              </Tooltip>
            </RenderOnRole>
          </Box>
        );
      },
    },
    {
      field: "keycloakUserId",
      headerName: Labels.getLabel("usersGrid.column.userId", "User ID"),
      width: 250,
      hide: true,
    },
    {
      field: "username",
      headerName: Labels.getLabel("usersGrid.column.username", "Username"),
      width: 350,
    },
    {
      field: "email",
      headerName: Labels.getLabel("usersGrid.column.email", "Email"),
      width: 350,
    },
    {
      field: "firstName",
      headerName: Labels.getLabel("usersGrid.column.firstName", "First Name"),
      width: 150,
    },
    {
      field: "lastName",
      headerName: Labels.getLabel("usersGrid.column.lastName", "Last Name"),
      width: 150,
    },
  ];

  const assignedLocationsColumns = [
    {
      field: "actions",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.actions",
        "Actions"
      ),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip
          title={Labels.getLabel(
            "usersGrid.locations.tooltip.removeLocation",
            "Remove Location"
          )}
        >
          <IconButton
            size="small"
            color="error"
            onClick={() => handleRemoveLocation(params.row.id)}
            disabled={
              removeLocationMutation.isLoading || addLocationMutation.isLoading
            }
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.description",
        "Description"
      ),
      flex: 1,
      minWidth: 250,
    },
    {
      field: "address_line_1",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.address",
        "Address"
      ),
      flex: 1,
      minWidth: 250,
      valueGetter: (params) => {
        const line2 = params.row.address_line_2;
        return line2
          ? `${params.row.address_line_1}, ${line2}`
          : params.row.address_line_1;
      },
    },
    {
      field: "city",
      headerName: Labels.getLabel("usersGrid.locations.column.city", "City"),
      width: 130,
    },
    {
      field: "state",
      headerName: Labels.getLabel("usersGrid.locations.column.state", "State"),
      width: 80,
    },
    {
      field: "zip",
      headerName: Labels.getLabel("usersGrid.locations.column.zip", "ZIP"),
      width: 100,
    },
    {
      field: "id",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.locationId",
        "Location ID"
      ),
      width: 100,
      hide: true,
    },
  ];

  const availableLocationsColumns = [
    {
      field: "actions",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.actions",
        "Actions"
      ),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip
          title={Labels.getLabel(
            "usersGrid.locations.tooltip.addLocation",
            "Add Location"
          )}
        >
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleAddLocation(params.row.id)}
            disabled={
              removeLocationMutation.isLoading || addLocationMutation.isLoading
            }
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.description",
        "Description"
      ),
      flex: 1,
      minWidth: 200,
    },
    {
      field: "address_line_1",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.address",
        "Address"
      ),
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => {
        const line2 = params.row.address_line_2;
        return line2
          ? `${params.row.address_line_1}, ${line2}`
          : params.row.address_line_1;
      },
    },
    {
      field: "city",
      headerName: Labels.getLabel("usersGrid.locations.column.city", "City"),
      width: 130,
    },
    {
      field: "state",
      headerName: Labels.getLabel("usersGrid.locations.column.state", "State"),
      width: 80,
    },
    {
      field: "zip",
      headerName: Labels.getLabel("usersGrid.locations.column.zip", "ZIP"),
      width: 100,
    },
    {
      field: "id",
      headerName: Labels.getLabel(
        "usersGrid.locations.column.locationId",
        "Location ID"
      ),
      width: 100,
    },
  ];

  const assignedSubdepartmentsColumns = [
    {
      field: "actions",
      headerName: Labels.getLabel(
        "usersGrid.subdepartments.column.actions",
        "Actions"
      ),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip
          title={Labels.getLabel(
            "usersGrid.subdepartments.tooltip.removeSubdepartment",
            "Remove Subdepartment"
          )}
        >
          <IconButton
            size="small"
            color="error"
            onClick={() => handleRemoveSubdepartmentClick(params.row)}
            disabled={
              removeSubdepartmentMutation.isLoading ||
              addSubdepartmentMutation.isLoading
            }
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "usersGrid.subdepartments.column.name",
        "Name"
      ),
      flex: 1,
      minWidth: 250,
      valueGetter: (params) => params.row.description || params.row.name,
    },
    {
      field: "idEmployer",
      headerName: Labels.getLabel(
        "usersGrid.subdepartments.column.employerId",
        "Employer ID"
      ),
      width: 150,
    },
  ];

  const availableSubdepartmentsColumns = [
    {
      field: "actions",
      headerName: Labels.getLabel(
        "usersGrid.subdepartments.column.actions",
        "Actions"
      ),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip
          title={Labels.getLabel(
            "usersGrid.subdepartments.tooltip.addSubdepartment",
            "Add Unit"
          )}
        >
          <IconButton
            size="small"
            color="primary"
            onClick={() =>
              handleAddSubdepartment(params.row.idEmployer || params.row.id)
            }
            disabled={
              removeSubdepartmentMutation.isLoading ||
              addSubdepartmentMutation.isLoading
            }
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "usersGrid.subdepartments.column.name",
        "Name"
      ),
      flex: 1,
      minWidth: 250,
      valueGetter: (params) => params.row.description || params.row.name,
    },
    {
      field: "idEmployer",
      headerName: Labels.getLabel(
        "usersGrid.subdepartments.column.employerId",
        "Employer ID"
      ),
      width: 150,
      valueGetter: (params) => params.row.idEmployer || params.row.id,
    },
  ];

  if (!employerId) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <PeopleIcon sx={{ fontSize: 64, color: "text.secondary" }} />
        <Typography variant="h6" color="text.secondary">
          {Labels.getLabel(
            "usersGrid.message.noEmployerSelected",
            "No employer selected"
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Box sx={{ mb: 2 }}>
        {/* Subdepartment Filter */}
        {showSubDepartmentFilter &&
          (isLoadingSubDepartments ||
            (subDepartmentsData?.data &&
              subDepartmentsData.data.length > 0)) && (
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                fullWidth
                options={subDepartmentsData?.data || []}
                getOptionLabel={(option) =>
                  `${option.description}-[${option.id}]`
                }
                value={
                  subDepartmentsData?.data?.find(
                    (sd) => sd.id === selectedSubDepartment
                  ) || null
                }
                onChange={(event, newValue) => {
                  setSelectedSubDepartment(newValue?.id || "");
                }}
                loading={isLoadingSubDepartments}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Labels.getLabel(
                      "usersGrid.filter.organizationalUnit",
                      "Filter by Organizational Unit"
                    )}
                    placeholder={Labels.getLabel(
                      "usersGrid.filter.allUsers",
                      "All Users"
                    )}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingSubDepartments ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Box>
          )}

        {/* Locations Filter */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            fullWidth
            options={availableLocationsData?.data || []}
            getOptionLabel={(option) =>
              option.id === -1
                ? option.description
                : `${option.description}, ${option.address_line_1}, ${option.city}, ${option.state}` ||
                  `Location ${option.id}`
            }
            value={selectedLocations}
            onChange={(event, newValue) => {
              setSelectedLocations(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Labels.getLabel(
                  "usersGrid.filter.selectLocation",
                  "Select Location"
                )}
                placeholder={Labels.getLabel(
                  "usersGrid.filter.selectLocationPlaceholder",
                  "Select location to search users"
                )}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Box>

        {/* Add User Button */}
        {showAddButton && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateUserDlg({ idEmployer: employerId })}
            sx={{
              backgroundColor: "#0d7f81",
              "&:hover": {
                backgroundColor: "#0a6b6d",
              },
            }}
          >
            {Labels.getLabel("usersGrid.button.addUsers", "Add Users")}
          </Button>
        )}
      </Box>

      {/* Users DataGrid */}
      <Box sx={{ height, width: "100%" }}>
        {isInitialLoadingUsers ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : usersData?.data ? (
          <>
            {usersData.data.tooManyResults && usersData.data.message && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {Labels.getLabel(
                  "usersGrid.message.tooManyResults",
                  `Too many results. Try entering more characters to narrow your search.`
                )}
              </Alert>
            )}
            <DataGrid
              rows={usersData.data.data || []}
              rowCount={usersData.data.total || 0}
              columns={usersColumns}
              getRowId={(row) => row.keycloakUserId}
              getRowHeight={() => 40}
              density="compact"
              disableDensitySelector
              paginationMode="server"
              filterMode="server"
              page={paginationModel.page}
              onPageChange={(newPage) =>
                setPaginationModel({ ...paginationModel, page: newPage })
              }
              pageSize={paginationModel.pageSize}
              onPageSizeChange={(newPageSize) =>
                setPaginationModel({ page: 0, pageSize: newPageSize })
              }
              rowsPerPageOptions={[1, 3, 100]}
              loading={isLoadingUsers}
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
                // Reset to first page when filter changes
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              components={{
                Toolbar: GridToolbar,
                NoRowsOverlay: () => {
                  // Check if search filter is too short
                  if (filterString && filterString.length < MIN_SEARCH_LENGTH) {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <PeopleIcon
                          sx={{ fontSize: 64, color: "text.secondary" }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          {Labels.getLabel(
                            "usersGrid.message.minSearchLength",
                            `Please enter at least ${MIN_SEARCH_LENGTH} characters to search`
                          )}
                        </Typography>
                      </Box>
                    );
                  }

                  // Default empty states
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <PeopleIcon
                        sx={{ fontSize: 64, color: "text.secondary" }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {usersData?.data?.tooManyResults
                          ? Labels.getLabel(
                              "usersGrid.message.tooManyResults",
                              "Please use the search filter above to narrow down results"
                            )
                          : filterString
                            ? Labels.getLabel(
                                "usersGrid.message.noUsersFound",
                                "No users found matching your search"
                              )
                            : Labels.getLabel(
                                "usersGrid.message.noUsersForLocation",
                                "No users found for this location"
                              )}
                      </Typography>
                    </Box>
                  );
                },
              }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: {
                    debounceMs: 500,
                    error:
                      filterString && filterString.length < MIN_SEARCH_LENGTH,
                    helperText:
                      filterString && filterString.length < MIN_SEARCH_LENGTH
                        ? Labels.getLabel(
                            "usersGrid.message.minSearchLength",
                            `Please enter at least ${MIN_SEARCH_LENGTH} characters to search`
                          )
                        : undefined,
                  },
                },
              }}
            />
          </>
        ) : null}
      </Box>

      {/* Create User Dialog */}
      {createUserDlg && (
        <CreateUser
          open={!!createUserDlg}
          setOpen={(isOpen) => {
            if (!isOpen) {
              setCreateUserDlg(null);
              queryClient.invalidateQueries({ queryKey: ["employerUsers"] });
            }
          }}
          employerId={createUserDlg.idEmployer}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {Labels.getLabel(
            "usersGrid.dialog.confirmRemoveUser",
            "Confirm Remove User"
          )}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {Labels.getLabel(
              "usersGrid.dialog.removeUserMessage",
              "Are you sure you want to remove user"
            )}{" "}
            <strong>{userToDelete?.username}</strong>{" "}
            {Labels.getLabel(
              "usersGrid.dialog.fromThisEmployer",
              "from this employer"
            )}
            {selectedLocations && (
              <>
                {" "}
                {Labels.getLabel(
                  "usersGrid.dialog.andSelectedLocation",
                  "and the selected location"
                )}
              </>
            )}
            ?
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteUserMutation.isLoading}
            >
              {Labels.getLabel("usersGrid.button.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deleteUserMutation.isLoading}
              startIcon={
                deleteUserMutation.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {Labels.getLabel("usersGrid.button.removeUser", "Remove User")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Disable User Confirmation Dialog */}
      <Dialog
        open={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {Labels.getLabel(
            "usersGrid.dialog.confirmDisableUser",
            "Confirm Disable User"
          )}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {Labels.getLabel(
              "usersGrid.dialog.disableUserMessage",
              "Are you sure you want to disable user"
            )}{" "}
            <strong>{userToDisable?.username}</strong>?
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              onClick={() => setDisableDialogOpen(false)}
              disabled={disableUserMutation.isLoading}
            >
              {Labels.getLabel("usersGrid.button.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleConfirmDisable}
              variant="contained"
              color="error"
              disabled={disableUserMutation.isLoading}
              startIcon={
                disableUserMutation.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <BlockIcon />
                )
              }
            >
              {Labels.getLabel("usersGrid.button.disableUser", "Disable User")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Locations Management Dialog */}
      <Dialog
        open={locationsDialog.open}
        onClose={handleCloseLocationsDialog}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "usersGrid.dialog.manageLocations",
                "Manage Locations"
              )}{" "}
              - {locationsDialog.userDisplayName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseLocationsDialog}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3, minHeight: 400 }}>
          {isLoadingLocations ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {Labels.getLabel(
                    "usersGrid.dialog.assignedLocations",
                    "Assigned Locations"
                  )}{" "}
                  ({userLocationsData?.data?.length || 0})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddLocationDialog(true)}
                  disabled={
                    unassignedLocations.length === 0 ||
                    addLocationMutation.isLoading ||
                    removeLocationMutation.isLoading
                  }
                  sx={{
                    backgroundColor: "#0d7f81",
                    "&:hover": {
                      backgroundColor: "#0a6b6d",
                    },
                  }}
                >
                  {Labels.getLabel(
                    "usersGrid.button.addLocation",
                    "Add Location"
                  )}
                </Button>
              </Box>
              <Box sx={{ height: 550, width: "100%" }}>
                {userLocationsData?.data &&
                userLocationsData.data.length > 0 ? (
                  <DataGrid
                    rows={userLocationsData.data}
                    columns={assignedLocationsColumns}
                    getRowId={(row) => row.id}
                    getRowHeight={() => 40}
                    density="compact"
                    disableDensitySelector
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      height: "100%",
                      gap: 2,
                    }}
                  >
                    <LocationOnIcon
                      sx={{ fontSize: 64, color: "text.secondary" }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      {Labels.getLabel(
                        "usersGrid.message.noLocationsAssigned",
                        "No locations assigned to this user"
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <Box
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            onClick={handleCloseLocationsDialog}
            disabled={
              addLocationMutation.isLoading || removeLocationMutation.isLoading
            }
          >
            {Labels.getLabel("usersGrid.button.close", "Close")}
          </Button>
        </Box>
      </Dialog>

      {/* Add Location Dialog */}
      <Dialog
        open={addLocationDialog}
        onClose={() => setAddLocationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AddIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel("usersGrid.dialog.addLocation", "Add Location")}{" "}
              - {locationsDialog.userDisplayName}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setAddLocationDialog(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3, minHeight: 400 }}>
          <Box sx={{ height: 400, width: "100%" }}>
            {isLoadingAvailableLocations ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : unassignedLocations.length > 0 ? (
              <DataGrid
                rows={unassignedLocations}
                columns={availableLocationsColumns}
                getRowId={(row) => row.id}
                getRowHeight={() => 40}
                density="compact"
                disableDensitySelector
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  height: "100%",
                  gap: 2,
                }}
              >
                <LocationOnIcon
                  sx={{ fontSize: 64, color: "text.secondary" }}
                />
                <Typography variant="body1" color="text.secondary">
                  {Labels.getLabel(
                    "usersGrid.message.noAvailableLocations",
                    "No available locations to add"
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Box
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button onClick={() => setAddLocationDialog(false)}>
            {Labels.getLabel("usersGrid.button.close", "Close")}
          </Button>
        </Box>
      </Dialog>

      {/* Subdepartments Management Dialog */}
      <Dialog
        open={subdepartmentsDialog.open}
        onClose={handleCloseSubdepartmentsDialog}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "usersGrid.dialog.manageSubdepartments",
                "Manage Units"
              )}{" "}
              - {subdepartmentsDialog.userDisplayName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseSubdepartmentsDialog}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3, minHeight: 400 }}>
          {isLoadingCompanies ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {Labels.getLabel(
                    "usersGrid.dialog.assignedSubdepartments",
                    "Assigned Units"
                  )}{" "}
                  ({userCompaniesData?.data?.length || 0})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddSubdepartmentDialog(true)}
                  disabled={
                    unassignedSubdepartments.length === 0 ||
                    addSubdepartmentMutation.isLoading ||
                    removeSubdepartmentMutation.isLoading
                  }
                  sx={{
                    backgroundColor: "#0d7f81",
                    "&:hover": {
                      backgroundColor: "#0a6b6d",
                    },
                  }}
                >
                  {Labels.getLabel(
                    "usersGrid.button.addSubdepartment",
                    "Add Unit"
                  )}
                </Button>
              </Box>
              <Box sx={{ height: 550, width: "100%" }}>
                {userCompaniesData?.data &&
                userCompaniesData.data.length > 0 ? (
                  <DataGrid
                    rows={userCompaniesData.data}
                    columns={assignedSubdepartmentsColumns}
                    getRowId={(row) =>
                      row.idEmployer || row.idCompany || row.id
                    }
                    getRowHeight={() => 40}
                    density="compact"
                    disableDensitySelector
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      height: "100%",
                      gap: 2,
                    }}
                  >
                    <BusinessIcon
                      sx={{ fontSize: 64, color: "text.secondary" }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      {Labels.getLabel(
                        "usersGrid.message.noSubdepartmentsAssigned",
                        "No subdepartments assigned to this user"
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <Box
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            onClick={handleCloseSubdepartmentsDialog}
            disabled={
              addSubdepartmentMutation.isLoading ||
              removeSubdepartmentMutation.isLoading
            }
          >
            {Labels.getLabel("usersGrid.button.close", "Close")}
          </Button>
        </Box>
      </Dialog>

      {/* Add Subdepartment Dialog */}
      <Dialog
        open={addSubdepartmentDialog}
        onClose={() => setAddSubdepartmentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AddIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel("usersGrid.dialog.addSubdepartment", "Add Unit")}{" "}
              - {subdepartmentsDialog.userDisplayName}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setAddSubdepartmentDialog(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3, minHeight: 400 }}>
          <Box sx={{ height: 400, width: "100%" }}>
            {isLoadingAvailableSubdepartments ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : unassignedSubdepartments.length > 0 ? (
              <DataGrid
                rows={unassignedSubdepartments}
                columns={availableSubdepartmentsColumns}
                getRowId={(row) => row.idEmployer || row.idCompany || row.id}
                getRowHeight={() => 40}
                density="compact"
                disableDensitySelector
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  height: "100%",
                  gap: 2,
                }}
              >
                <BusinessIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                <Typography variant="body1" color="text.secondary">
                  {Labels.getLabel(
                    "usersGrid.message.noAvailableSubdepartments",
                    "No available subdepartments to add"
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Box
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button onClick={() => setAddSubdepartmentDialog(false)}>
            {Labels.getLabel("usersGrid.button.close", "Close")}
          </Button>
        </Box>
      </Dialog>

      {/* Remove Subdepartment Confirmation Dialog */}
      <Dialog
        open={removeSubdepartmentDialog.open}
        onClose={() =>
          setRemoveSubdepartmentDialog({
            open: false,
            employerId: null,
            employerName: null,
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {Labels.getLabel(
            "usersGrid.dialog.confirmRemoveSubdepartment",
            "Confirm Remove Unit"
          )}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {Labels.getLabel(
              "usersGrid.dialog.removeSubdepartmentMessage",
              "Are you sure you want to remove unit access"
            )}{" "}
            <strong>{removeSubdepartmentDialog.employerName}</strong>{" "}
            {Labels.getLabel("usersGrid.dialog.fromThisUser", "from this user")}
            ?
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              onClick={() =>
                setRemoveSubdepartmentDialog({
                  open: false,
                  employerId: null,
                  employerName: null,
                })
              }
              disabled={removeSubdepartmentMutation.isLoading}
            >
              {Labels.getLabel("usersGrid.button.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleConfirmRemoveSubdepartment}
              variant="contained"
              color="error"
              disabled={removeSubdepartmentMutation.isLoading}
              startIcon={
                removeSubdepartmentMutation.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <RemoveCircleOutlineIcon />
                )
              }
            >
              {Labels.getLabel(
                "usersGrid.button.removeSubdepartment",
                "Remove Unit"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {Labels.getLabel(
            "usersGrid.dialog.confirmResetPassword",
            "Confirm Reset Password"
          )}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {Labels.getLabel(
              "usersGrid.dialog.resetPasswordMessage",
              "Are you sure you want to reset the password for user"
            )}{" "}
            <strong>{userToResetPassword?.username}</strong>?
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              onClick={() => setResetPasswordDialogOpen(false)}
              disabled={resetPasswordMutation.isLoading}
            >
              {Labels.getLabel("usersGrid.button.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleConfirmResetPassword}
              variant="contained"
              color="warning"
              disabled={resetPasswordMutation.isLoading}
              startIcon={
                resetPasswordMutation.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <LockResetIcon />
                )
              }
            >
              {Labels.getLabel(
                "usersGrid.button.resetPassword",
                "Reset Password"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Keycloak Roles Dialog */}
      <Dialog
        open={rolesDialog.open}
        onClose={handleCloseRolesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: "info.lighter",
                  color: "info.main",
                }}
              >
                <AssignmentIndIcon />
              </Box>
              <Box minWidth={0}>
                <Typography variant="overline" color="text.secondary">
                  Assigned realm roles
                </Typography>
                <Typography variant="h6" noWrap>
                  {rolesDialog.userDisplayName}
                </Typography>
              </Box>
            </Box>
            {!isLoadingRoles && !isRolesError && (
              <Chip
                label={`${sortedUserRoles.length} ${
                  sortedUserRoles.length === 1 ? "role" : "roles"
                }`}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "grey.50", pt: 2 }}>
          {isLoadingRoles ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : isRolesError ? (
            <Alert severity="error">Unable to load Keycloak roles.</Alert>
          ) : sortedUserRoles.length ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This user can access the following application roles.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1,
                }}
              >
                {sortedUserRoles.map((role) => (
                  <Box
                    key={role}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <AssignmentIndIcon
                      sx={{ color: "info.main", fontSize: 18, flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {role}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <AssignmentIndIcon
                sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
              />
              <Typography color="text.secondary">
                No realm roles are assigned to this user.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 3,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button onClick={handleCloseRolesDialog}>Close</Button>
        </Box>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
